import { parse } from 'qs';
import Store from 'repatch';

import { GraphQLQueryException } from '../adapter/GraphQLClient';
import State from '../core/State';
import { OrganizationDetailFragment } from '../generated/graphql';
import ErrorPresenter from '../service/ErrorPresenter';
import IntercomService from '../service/IntercomService';
import OrganizationWebApi from '../service/persistent/OrganizationWebApi';
import ProjectTypeWebApi from '../service/persistent/ProjectTypeWebApi';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import { dispose, finishFetchingProjectTypes, setProjectTypes, startFetchingProjectTypes } from '../thunk/projectTypes';

class ListProjectTypesUsecase {
  private readonly store: Store<State>;
  private readonly errorPresenter: ErrorPresenter;
  private readonly intercomService: IntercomService;
  private readonly organizationWebApi: OrganizationWebApi;
  private readonly projectTypeWebApi: ProjectTypeWebApi;

  constructor({
    store,
    errorPresenter,
    intercomService,
    organizationWebApi,
    projectTypeWebApi,
  }: {
    store: Store<State>;
    errorPresenter: ErrorPresenter;
    intercomService: IntercomService;
    organizationWebApi: OrganizationWebApi;
    projectTypeWebApi: ProjectTypeWebApi;
  }) {
    this.store = store;
    this.errorPresenter = errorPresenter;
    this.intercomService = intercomService;
    this.organizationWebApi = organizationWebApi;
    this.projectTypeWebApi = projectTypeWebApi;
  }

  public requestAnotherProjectType = (): void => {
    this.intercomService.openNewMessage();
  };

  public prepareProjectTypes = async (): Promise<void> => {
    this.store.dispatch(startFetchingProjectTypes());
    const { language } = this.store.getState();
    const query = parse(location.search.slice(1));
    const organizationId = query.org as string;

    const projectTypes = await this.projectTypeWebApi.getAllByOrganization({
      language,
      organizationId,
    });

    this.store.dispatch(finishFetchingProjectTypes());
    this.store.dispatch(setProjectTypes(projectTypes));
  };

  public prepare = async (): Promise<void> => {
    const state = this.store.getState();
    const { location, projectTypes } = state;

    if (projectTypes.projectTypesFetching === 0 && projectTypes.projectTypes.length === 0) {
      this.prepareProjectTypes();
    }

    let organization!: OrganizationDetailFragment;

    const query = parse(location.search.slice(1));
    const organizationId = query.org as string;

    if (organizationId == null) return;

    try {
      organization = await this.organizationWebApi.getById(organizationId);
    } catch (err) {
      if (err instanceof GraphQLQueryException) {
        this.errorPresenter.showAsNonFatal(new Error('message.fetch_creations_has_failed'));
      } else {
        // TODO: show message for user: 404 and 401 in fetching organization
        this.errorPresenter.showAsFatal(new Error('message.fetch_creations_has_failed'));

        return;
      }

      throw err;
    }

    if (organization) {
      this.store.dispatch(setLastAccessedOrganization(organization));
    }
  };

  public dispose = (): void => {
    this.store.dispatch(dispose());
  };
}

export default ListProjectTypesUsecase;
